import React from 'react';
import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import { useBizflexAppUrls } from '../../../utils/useBizflexAppUrls';
import { brandGradientButtonStyles } from '../../../lib/brandGradient';
import {
  ANDROID_APP_URL,
  IOS_APP_URL,
} from '../../../pages/Invoice/invoiceContent';
import {
  PAYMENT_LINK_HERO_IMAGE,
  paymentLinkHero,
} from '../../../pages/PaymentLink/paymentLinkContent';

const appStoreButtonStyles = {
  bg: '#0A1628',
  color: '#fff',
  border: '1px solid rgba(255, 255, 255, 0.45)',
  borderRadius: '12px',
  h: { base: '48px', md: '52px' },
  px: { base: '14px', md: '16px' },
  fontWeight: 400,
  fontSize: { base: '12px', md: '14px' },
  whiteSpace: 'nowrap',
  _hover: { bg: 'rgba(15, 23, 43, 0.85)' },
};

const PaymentLinkHero = () => {
  const { registerUrl } = useBizflexAppUrls();
  const {
    badge,
    titleLine1,
    titleHighlight,
    titleLine2,
    description,
    mobileDescriptionLine2,
    ctaLabel,
  } = paymentLinkHero;

  return (
    <Box
      color="#FFFFFF"
      fontFamily="Poppins"
      w="100%"
      py={{ base: '32px', md: '60px', lg: '64px' }}
      pb={{ base: '32px', md: '56px' }}
    >
      <Flex
        align="center"
        gap={{ base: '32px', md: '48px', lg: '64px' }}
        flexDir={{ base: 'column', md: 'row' }}
        w="100%"
      >
        <Box
          w={{ base: '100%', md: '50%' }}
          display="flex"
          flexDirection="column"
          alignItems={{ base: 'center', md: 'flex-start' }}
        >
          <Box
            mb={{ base: '12px', md: '16px' }}
            w={{ base: '100%', md: 'fit-content' }}
          >
            <Text
              fontSize="10px"
              fontWeight={400}
              color="#FFFFFF"
              textAlign="center"
              bg="#FFFFFF10"
              px={{ base: '10px', md: '12px' }}
              py={{ base: '6px', md: '4px' }}
              borderRadius="full"
              lineHeight="1.2"
              whiteSpace="nowrap"
              letterSpacing={{ base: '0.06em', md: 'normal' }}
              display="inline-block"
              w={{ base: '100%', md: 'auto' }}
            >
              {badge}
            </Text>
          </Box>

          <Text
            as="h1"
            fontSize={{ base: '28px', md: '44px', lg: '52px' }}
            fontWeight={700}
            lineHeight={{ base: '36px', md: '56px', lg: '66px' }}
            textAlign={{ base: 'center', md: 'start' }}
            mb={{ base: '16px', md: '20px' }}
            maxW={{ md: '640px' }}
          >
            <Box as="span" display={{ base: 'block', md: 'none' }}>
              Accept Payments
              <br />
              <Text as="span" color="#FFB900">
                {titleHighlight}
              </Text>{' '}
              with Payment
              <br />
              Links
            </Box>
            <Box as="span" display={{ base: 'none', md: 'block' }}>
              {titleLine1}
              <br />
              <Text as="span" color="#FFB900">
                {titleHighlight}
              </Text>{' '}
              with
              <br />
              Payment Links
            </Box>
          </Text>

          <Text
            color="#94A3B8"
            fontSize={{ base: '11px', md: '14px' }}
            lineHeight={{ base: '18px', md: '26px', lg: '28px' }}
            textAlign={{ base: 'center', md: 'start' }}
            mb={{ base: '24px', md: '36px' }}
            maxW={{ base: '100%', md: '600px' }}
          >
            <Box
              as="span"
              display={{ base: 'block', md: 'none' }}
              fontSize={{ base: '10px', md: '14px' }}
              lineHeight={{ base: '18px', md: '26px' }}
            >
              <Text as="span" display="block">
                Generate a link, share it anywhere—social media, SMS,{' '}
                <Text as="span" whiteSpace="nowrap">
                  or email—and
                </Text>
              </Text>
              <Text as="span" display="block" whiteSpace="nowrap">
                {mobileDescriptionLine2}
              </Text>
            </Box>
            <Box as="span" display={{ base: 'none', md: 'inline' }}>
              {description}
            </Box>
          </Text>

          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: '12px', md: '16px' }}
            justifyContent={{ base: 'stretch', md: 'flex-start' }}
            align={{ base: 'stretch', md: 'center' }}
            w={{ base: '100%', md: 'auto' }}
          >
            <Button
              as="a"
              href={registerUrl}
              target="_blank"
              rel="noreferrer"
              {...brandGradientButtonStyles}
              borderRadius="12px"
              h={{ base: '48px', md: '52px' }}
              px={{ base: '20px', md: '28px' }}
              fontWeight={500}
              fontSize={{ base: '14px', md: '16px' }}
              w={{ base: '100%', md: 'auto' }}
            >
              {ctaLabel}
            </Button>

            <Flex
              display={{ base: 'flex', md: 'contents' }}
              direction="row"
              gap="12px"
              w="100%"
            >
              <Button
                as="a"
                href={ANDROID_APP_URL}
                target="_blank"
                rel="noreferrer"
                {...appStoreButtonStyles}
                flex={{ base: 1, md: 'unset' }}
                leftIcon={
                  <Image
                    src="/assets/android.svg"
                    w={{ base: '16px', md: '18px' }}
                    h={{ base: '12px', md: '13px' }}
                    alt="android"
                  />
                }
              >
                Android
              </Button>

              <Button
                as="a"
                href={IOS_APP_URL}
                target="_blank"
                rel="noreferrer"
                {...appStoreButtonStyles}
                flex={{ base: 1, md: 'unset' }}
                leftIcon={
                  <Image
                    src="/assets/apple.svg"
                    w={{ base: '16px', md: '18px' }}
                    h={{ base: '12px', md: '13px' }}
                    alt="apple"
                  />
                }
              >
                iOS
              </Button>
            </Flex>
          </Flex>
        </Box>

        <Box
          w={{ base: '100%', md: '50%' }}
          display="flex"
          justifyContent={{ base: 'center', md: 'flex-end' }}
        >
          <Image
            src={PAYMENT_LINK_HERO_IMAGE}
            alt="Business owner accepting payments with BizFlex payment links"
            w="100%"
            maxW={{ base: '100%', md: '580px' }}
            maxH={{ base: '260px', md: '460px', lg: '460px' }}
            borderRadius={{ base: '20px', md: '24px' }}
            objectFit="cover"
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default PaymentLinkHero;
